import { createContext, useContext, useEffect, useState, type ReactNode, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { useUser } from './UserContext'
import { RealtimeChannel } from '@supabase/supabase-js'
import { getDeviceId, isFromThisDevice } from '../utils/deviceFingerprint'
import type {
    SyncStatus,
    MessageSentPayload,
    ConversationChangedPayload,
    RemoteTypingState,
    RemoteDraft
} from '../types/realtime'

interface Message {
    id: string
    role: 'user' | 'assistant' | 'system' | 'tool'
    content: string
    conversation_id: string
    created_at: string
    device_id?: string
}

interface SyncContextType {
    // Mensagens
    broadcastMessage: (messageId: string, conversationId: string, role: 'user' | 'assistant', content: string) => void
    onRemoteMessage: (callback: (message: Message) => void) => (() => void)

    // Conversas
    broadcastConversationChange: (conversationId: string) => void
    onRemoteConversationChange: (callback: (conversationId: string) => void) => (() => void)

    // Digitando (já existente)
    broadcastTyping: (isTyping: boolean, conversationId: string) => void
    remoteTyping: RemoteTypingState

    // Rascunhos (já existente)
    broadcastDraft: (content: string, conversationId: string) => void
    remoteDraft: RemoteDraft | null

    // Status
    syncStatus: SyncStatus
    syncMessage?: string
}

const SyncContext = createContext<SyncContextType | undefined>(undefined)

export function SyncProvider({ children }: { children: ReactNode }) {
    const { user } = useUser()
    const [channel, setChannel] = useState<RealtimeChannel | null>(null)
    const [remoteTyping, setRemoteTyping] = useState<RemoteTypingState>({})
    const [remoteDraft, setRemoteDraft] = useState<RemoteDraft | null>(null)
    const [syncStatus, setSyncStatus] = useState<SyncStatus>(() => navigator.onLine ? 'synced' : 'offline')
    const [syncMessage, setSyncMessage] = useState<string>()

    // Callbacks refs para eventos
    const messageCallbackRef = useRef<((message: Message) => void) | null>(null)
    const conversationChangeCallbackRef = useRef<((conversationId: string) => void) | null>(null)

    const deviceId = getDeviceId()

    // ============================================
    // Setup do Canal de Realtime
    // ============================================
    useEffect(() => {
        if (!user) return

        const newChannel = supabase.channel(`user_sync:${user.id}`)

        // Broadcast listeners
        newChannel
            // Mensagens
            .on('broadcast', { event: 'message_sent' }, ({ payload }: { payload: MessageSentPayload }) => {
                // Ignora se veio deste dispositivo
                if (isFromThisDevice(payload.deviceId)) return

                console.log('[Sync] Remote message received:', payload)

                // Notifica via callback
                if (messageCallbackRef.current) {
                    messageCallbackRef.current({
                        id: payload.messageId,
                        role: payload.role,
                        content: payload.content,
                        conversation_id: payload.conversationId,
                        created_at: payload.timestamp,
                        device_id: payload.deviceId
                    })
                }
            })

            // Mudança de conversa
            .on('broadcast', { event: 'conversation_changed' }, ({ payload }: { payload: ConversationChangedPayload }) => {
                // Ignora se veio deste dispositivo
                if (isFromThisDevice(payload.deviceId)) return

                console.log('[Sync] Remote conversation change:', payload)

                // Notifica via callback
                if (conversationChangeCallbackRef.current) {
                    conversationChangeCallbackRef.current(payload.conversationId)
                }
            })

            // Digitando
            .on('broadcast', { event: 'typing' }, ({ payload }) => {
                // Ignora se veio deste dispositivo
                if (payload.deviceId && isFromThisDevice(payload.deviceId)) return

                setRemoteTyping(prev => ({
                    ...prev,
                    [payload.conversationId]: {
                        isTyping: payload.isTyping,
                        deviceId: payload.deviceId || 'unknown'
                    }
                }))
            })

            // Rascunhos
            .on('broadcast', { event: 'draft_update' }, ({ payload }) => {
                // Ignora se veio deste dispositivo
                if (payload.deviceId && isFromThisDevice(payload.deviceId)) return

                setRemoteDraft({
                    conversationId: payload.conversationId,
                    content: payload.content,
                    deviceId: payload.deviceId || 'unknown',
                    timestamp: payload.timestamp
                })
            })
            .subscribe((status) => {
                if (status === 'SUBSCRIBED') {
                    setChannel(newChannel)
                }
            })

        return () => {
            supabase.removeChannel(newChannel)
            setChannel(null)
        }
    }, [user, deviceId])

    // ============================================
    // Database Subscriptions (Postgres Changes)
    // ============================================
    useEffect(() => {
        if (!user) return

        // Subscription para INSERT em messages
        const messagesChannel = supabase
            .channel('db-messages-changes')
            .on(
                'postgres_changes' as const,
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'messages'
                },
                (payload: { new: Message }) => {
                    const newMessage = payload.new

                    // Ignora se veio deste dispositivo
                    if (newMessage.device_id && isFromThisDevice(newMessage.device_id)) {
                        return
                    }

                    console.log('[Sync] Database INSERT detected:', newMessage)

                    // Notifica via callback
                    if (messageCallbackRef.current) {
                        messageCallbackRef.current(newMessage)
                    }
                }
            )
            .subscribe()

        return () => {
            supabase.removeChannel(messagesChannel)
        }
    }, [user])

    // ============================================
    // Detectar status offline/online
    // ============================================
    useEffect(() => {
        const handleOnline = () => {
            setSyncStatus('synced')
            setSyncMessage('Conexão restaurada')
            setTimeout(() => setSyncMessage(undefined), 3000)
        }
        const handleOffline = () => {
            setSyncStatus('offline')
            setSyncMessage('Sem conexão')
        }

        window.addEventListener('online', handleOnline)
        window.addEventListener('offline', handleOffline)

        return () => {
            window.removeEventListener('online', handleOnline)
            window.removeEventListener('offline', handleOffline)
        }
    }, [])

    // ============================================
    // Broadcast Functions
    // ============================================

    const broadcastMessage = useCallback((
        messageId: string,
        conversationId: string,
        role: 'user' | 'assistant',
        content: string
    ) => {
        if (!channel) return

        const payload: MessageSentPayload = {
            messageId,
            conversationId,
            deviceId,
            role,
            content,
            timestamp: new Date().toISOString()
        }

        setSyncStatus('syncing')
        channel.send({
            type: 'broadcast',
            event: 'message_sent',
            payload
        })

        setTimeout(() => setSyncStatus('synced'), 500)
    }, [channel, deviceId])

    const broadcastConversationChange = useCallback((conversationId: string) => {
        if (!channel) return

        const payload: ConversationChangedPayload = {
            conversationId,
            deviceId,
            timestamp: Date.now()
        }

        setSyncStatus('syncing')
        channel.send({
            type: 'broadcast',
            event: 'conversation_changed',
            payload
        })

        setTimeout(() => setSyncStatus('synced'), 500)
    }, [channel, deviceId])

    const broadcastTyping = useCallback((isTyping: boolean, conversationId: string) => {
        if (!channel) return

        setSyncStatus('syncing')
        channel.send({
            type: 'broadcast',
            event: 'typing',
            payload: { isTyping, conversationId, deviceId }
        })

        setTimeout(() => setSyncStatus('synced'), 500)
    }, [channel, deviceId])

    const broadcastDraft = useCallback((content: string, conversationId: string) => {
        if (!channel) return

        setSyncStatus('syncing')
        channel.send({
            type: 'broadcast',
            event: 'draft_update',
            payload: {
                content,
                conversationId,
                deviceId,
                timestamp: Date.now()
            }
        })

        setTimeout(() => setSyncStatus('synced'), 500)
    }, [channel, deviceId])

    // ============================================
    // Callback Registration
    // ============================================

    const onRemoteMessage = useCallback((callback: (message: Message) => void) => {
        messageCallbackRef.current = callback
        return () => { messageCallbackRef.current = null }
    }, [])

    const onRemoteConversationChange = useCallback((callback: (conversationId: string) => void) => {
        conversationChangeCallbackRef.current = callback
        return () => { conversationChangeCallbackRef.current = null }
    }, [])

    return (
        <SyncContext.Provider value={{
            broadcastMessage,
            onRemoteMessage,
            broadcastConversationChange,
            onRemoteConversationChange,
            broadcastTyping,
            broadcastDraft,
            remoteTyping,
            remoteDraft,
            syncStatus,
            syncMessage
        }}>
            {children}
        </SyncContext.Provider>
    )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSync() {
    const context = useContext(SyncContext)
    if (context === undefined) {
        throw new Error('useSync must be used within a SyncProvider')
    }
    return context
}
